import { useState } from "react";
import { useAuth } from "hooks/useAuth";
import { useUserContext } from "contexts/UserContext";
import { useIntl } from "react-intl";
import { FORM_ERROR } from "final-form";

import { LoginFormValues } from "./types";
import { getGetSecondsUntilNextloginAttempt } from "./utils";

export const useLoginForm = () => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [secondsUntilNextloginAttempt, setSecondsUntilNextloginAttempt] =
    useState(0);
  const { signIn, answerChallenge } = useAuth();
  const { refetch } = useUserContext();

  const onSubmit = async (values: LoginFormValues) => {
    try {
      setLoading(true);

      const { phoneNumber, pinCode } = values;

      switch (step) {
        case 0: {
          const user = await signIn(phoneNumber);

          setSecondsUntilNextloginAttempt(
            getGetSecondsUntilNextloginAttempt(user)
          );

          setStep(1);

          break;
        }
        case 1: {
          await answerChallenge(pinCode);
          await refetch();

          break;
        }
        default: {
          throw new Error("Unhandled");
        }
      }

      return undefined;
    } catch {
      switch (step) {
        case 0: {
          return {
            phoneNumber: intl.formatMessage({
              defaultMessage:
                "User does not exits or login rate limit has been reached. Please try again.",
            }),
          };
        }
        case 1: {
          return {
            pinCode: intl.formatMessage({
              defaultMessage: "Verification code is invalid.",
            }),
          };
        }
        default: {
          return {
            [FORM_ERROR]: intl.formatMessage({
              defaultMessage: "Something went wrong. Please try again.",
            }),
          };
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async (phoneNumber: string) => {
    try {
      setLoading(true);

      const user = await signIn(phoneNumber);

      setSecondsUntilNextloginAttempt(getGetSecondsUntilNextloginAttempt(user));
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    onSubmit,
    resendCode,
    loading,
    step,
    secondsUntilNextloginAttempt,
  };
};
